import React, { useState, useEffect } from 'react';
import { get, post, put } from '../../utils/apiClient';
import { clearCacheKey } from '../../utils/cacheUtils';
import Base64Image from '../Base64Image';
import {
  AdminSectionTitle,
  AdminSectionText,
  AdminLoginLabel,
  AdminLoginInput,
  AdminLoginButton,
  AdminButton,
  AdminSaveButton,
} from '../../styles/AdminPageStyles';

const AdminContactPage = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [uploadingHeader, setUploadingHeader] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  useEffect(() => {
    const loadPage = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await get('/contact');
        setPageData(data);
      } catch (err) {
        setError(err && err.message ? err.message : 'Failed to load contact page data.');
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, []);

  const handleHeaderTitleChange = (e) => {
    const { value } = e.target;
    setPageData((prev) => ({
      ...(prev || {}),
      header: {
        ...((prev && prev.header) || {}),
        title: value,
      },
    }));
  };

  const handleHeaderBackgroundUpload = async (file) => {
    if (!file) return;
    setUploadingHeader(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('image', file);
      await post('/contact/header/background-image', formData);
      const data = await get('/contact');
      setPageData(data);
      clearCacheKey('contact_data');
    } catch (err) {
      setUploadError(
        (err && err.message) || 'Failed to upload header background.',
      );
    } finally {
      setUploadingHeader(false);
    }
  };

  const handleContactFieldChange = (field, value) => {
    setPageData((prev) => ({
      ...(prev || {}),
      contactInfo: {
        ...((prev && prev.contactInfo) || {}),
        [field]: value,
      },
    }));
  };

  const handleLocationFieldChange = (field, value) => {
    setPageData((prev) => ({
      ...(prev || {}),
      location: {
        ...((prev && prev.location) || {}),
        [field]: value,
      },
    }));
  };

  const updateSocialLink = (index, field, value) => {
    setPageData((prev) => {
      const next = { ...(prev || {}) };
      const links = Array.isArray(next.socialLinks) ? [...next.socialLinks] : [];
      const existing = links[index] || {};
      links[index] = {
        ...existing,
        [field]: value,
      };
      next.socialLinks = links;
      return next;
    });
  };

  const addSocialLink = () => {
    setPageData((prev) => {
      const next = { ...(prev || {}) };
      const links = Array.isArray(next.socialLinks) ? [...next.socialLinks] : [];
      links.push({ platform: '', url: '' });
      next.socialLinks = links;
      return next;
    });
  };

  const removeSocialLink = (index) => {
    setPageData((prev) => {
      if (!prev || !Array.isArray(prev.socialLinks)) return prev;
      const links = prev.socialLinks.filter((_, i) => i !== index);
      return {
        ...prev,
        socialLinks: links,
      };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!pageData) return;

    setSaving(true);
    setError(null);
    setSaveSuccess(null);

    try {
      const payload = {
        header: {
          ...(pageData.header || {}),
          title: (pageData.header && pageData.header.title) || 'Contact',
        },
        contactInfo: pageData.contactInfo || {},
        location: pageData.location || {},
        socialLinks: Array.isArray(pageData.socialLinks) ? pageData.socialLinks : [],
      };

      const updated = await put('/contact', payload);
      setPageData(updated);
      clearCacheKey('contact_data');
      setSaveSuccess('Contact page saved successfully.');
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to save contact page.');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !pageData) {
    return (
      <>
        <AdminSectionTitle>Contact Page CMS</AdminSectionTitle>
        <AdminSectionText>Loading contact page data...</AdminSectionText>
      </>
    );
  }

  const header = pageData && pageData.header ? pageData.header : null;
  const contactInfo = pageData ? pageData.contactInfo : null;
  const location = pageData ? pageData.location : null;
  const socialLinks =
    pageData && Array.isArray(pageData.socialLinks) ? pageData.socialLinks : [];

  return (
    <>
      <AdminSectionTitle>Contact Page CMS</AdminSectionTitle>

      <form onSubmit={handleSave}>
        <div style={{ marginBottom: 16 }}>
          <AdminLoginLabel htmlFor="contact-header-title">Header Title</AdminLoginLabel>
          <AdminLoginInput
            id="contact-header-title"
            value={(header && header.title) || ''}
            onChange={handleHeaderTitleChange}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminSectionTitle style={{ fontSize: '1rem', marginBottom: 8 }}>
            Header Background Image
          </AdminSectionTitle>
          {header && header.backgroundImage && (
            <div style={{ marginBottom: 8 }}>
              <Base64Image
                image={header.backgroundImage}
                alt={header.title || 'Contact header'}
                style={{ width: 260, borderRadius: 6 }}
              />
            </div>
          )}
          <AdminLoginLabel htmlFor="contact-header-bg">Upload Header Background</AdminLoginLabel>
          <input
            id="contact-header-bg"
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
              if (file) {
                handleHeaderBackgroundUpload(file);
                e.target.value = '';
              }
            }}
            style={{ fontSize: '0.9rem' }}
          />
          {uploadingHeader && (
            <AdminSectionText style={{ fontSize: '0.85rem' }}>
              Uploading header background...
            </AdminSectionText>
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminSectionTitle style={{ fontSize: '1rem', marginBottom: 8 }}>
            Contact Information
          </AdminSectionTitle>

          <div style={{ marginBottom: 8 }}>
            <AdminLoginLabel htmlFor="contact-address">Address</AdminLoginLabel>
            <textarea
              id="contact-address"
              rows={2}
              value={(contactInfo && contactInfo.address) || ''}
              onChange={(e) => handleContactFieldChange('address', e.target.value)}
              style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
            />
          </div>

          <div style={{ marginBottom: 8 }}>
            <AdminLoginLabel htmlFor="contact-phone">Phone</AdminLoginLabel>
            <AdminLoginInput
              id="contact-phone"
              value={(contactInfo && contactInfo.phone) || ''}
              onChange={(e) => handleContactFieldChange('phone', e.target.value)}
            />
          </div>

          <div style={{ marginBottom: 8 }}>
            <AdminLoginLabel htmlFor="contact-email">Email</AdminLoginLabel>
            <AdminLoginInput
              id="contact-email"
              value={(contactInfo && contactInfo.email) || ''}
              onChange={(e) => handleContactFieldChange('email', e.target.value)}
            />
          </div>

          <div style={{ marginBottom: 8 }}>
            <AdminLoginLabel htmlFor="contact-website">Website</AdminLoginLabel>
            <AdminLoginInput
              id="contact-website"
              value={(contactInfo && contactInfo.website) || ''}
              onChange={(e) => handleContactFieldChange('website', e.target.value)}
            />
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminSectionTitle style={{ fontSize: '1rem', marginBottom: 8 }}>
            Location / Map
          </AdminSectionTitle>

          <div style={{ marginBottom: 8 }}>
            <AdminLoginLabel htmlFor="contact-map-embed">Map Embed Code (optional)</AdminLoginLabel>
            <textarea
              id="contact-map-embed"
              rows={3}
              value={(location && location.mapEmbedCode) || ''}
              onChange={(e) => handleLocationFieldChange('mapEmbedCode', e.target.value)}
              style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
            />
          </div>

          <div style={{ marginBottom: 8 }}>
            <AdminLoginLabel htmlFor="contact-map-url">Map URL (optional)</AdminLoginLabel>
            <AdminLoginInput
              id="contact-map-url"
              value={(location && location.mapUrl) || ''}
              onChange={(e) => handleLocationFieldChange('mapUrl', e.target.value)}
            />
          </div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <AdminSectionTitle style={{ fontSize: '1rem', marginBottom: 8 }}>
            Social Links (optional)
          </AdminSectionTitle>

          {socialLinks.length === 0 && (
            <AdminSectionText style={{ fontSize: '0.9rem' }}>
              No social links configured yet.
            </AdminSectionText>
          )}

          {socialLinks.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              {socialLinks.map((link, index) => (
                <div
                  key={`${link.platform || 'social'}-${index}`}
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: 8,
                    padding: 10,
                    marginBottom: 8,
                    fontSize: '0.9rem',
                  }}
                >
                  <div style={{ marginBottom: 6 }}>
                    <AdminLoginLabel htmlFor={`social-platform-${index}`}>
                      Platform
                    </AdminLoginLabel>
                    <AdminLoginInput
                      id={`social-platform-${index}`}
                      value={link.platform || ''}
                      onChange={(e) => updateSocialLink(index, 'platform', e.target.value)}
                    />
                  </div>
                  <div style={{ marginBottom: 6 }}>
                    <AdminLoginLabel htmlFor={`social-url-${index}`}>
                      URL
                    </AdminLoginLabel>
                    <AdminLoginInput
                      id={`social-url-${index}`}
                      value={link.url || ''}
                      onChange={(e) => updateSocialLink(index, 'url', e.target.value)}
                    />
                  </div>
                  <AdminButton type="button" onClick={() => removeSocialLink(index)}>
                    Remove Social Link
                  </AdminButton>
                </div>
              ))}
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <AdminButton type="button" onClick={addSocialLink}>
              Add Social Link
            </AdminButton>
          </div>
        </div>

        <div style={{ marginTop: 32, display: 'flex', alignItems: 'center', gap: '16px' }}>
          <AdminSaveButton type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save Contact Page'}
          </AdminSaveButton>
          {saveSuccess && (
            <span style={{ color: '#1b5e20', fontWeight: '500' }}>{saveSuccess}</span>
          )}
          {error && (
            <span style={{ color: '#b71c1c', fontWeight: '500' }}>{error}</span>
          )}
          {uploadError && (
            <span style={{ color: '#b71c1c', fontWeight: '500' }}>{uploadError}</span>
          )}
        </div>
      </form>
    </>
  );
};

export default AdminContactPage;
